import type { OrderResponse } from '~/types/ecommerce';

export default defineNuxtPlugin({
  name: 'kygunco:analytics',
  dependsOn: ['kygunco:bus', 'kygunco:ecommerce'],
  parallel: true,
  setup: () => {
    const { $bus, $ecommerce } = useNuxtApp();

    const authStore = useAuthStore();

    /**
     * Product detail page view → GA4 `view_item`
     * @event product:view
     * @param {Product} product
     * @param {Variation} [variation]
     */
    $bus.on('product:view', (product, variation) =>
      useTrackEvent('view_item', {
        currency: 'USD',
        value: product.price,
        items: [
          {
            item_id: product.id,
            item_name: product.name,
            item_brand: product.manufacturer?.name,
            item_variant: variation?.identifier,
            price: product.price,
          },
        ],
      }),
    );

    /**
     * Add product/variation to cart → GA4 `add_to_cart`
     * @event product:add
     * @param {Product} product
     * @param {Variation} [variation]
     * @param {number} [quantity=1]
     */
    $bus.on('product:add', (product, variation, quantity) =>
      useTrackEvent('add_to_cart', {
        currency: 'USD',
        value: (variation?.price ?? product.price) * (quantity ?? 1),
        items: [
          {
            item_id: product.id,
            item_name: product.name,
            item_brand: product.manufacturer?.name,
            item_variant: variation?.identifier,
            price: variation?.price ?? product.price,
            quantity,
          },
        ],
      }),
    );

    /**
     * Catalog search → GA4 `search`
     * @event catalog:search
     * @param {string} term - The search term typed by the user.
     */
    $bus.on('catalog:search', term => useTrackEvent('search', { search_term: term }));

    /**
     * View list/grid of products → GA4 `view_item_list`
     * @event catalog:view
     * @param {Product[]} products - Products shown in the list.
     * @param {string} [collection] - Logical list name (e.g., category).
     */
    $bus.on('catalog:view', (products, collection) =>
      useTrackEvent('view_item_list', {
        item_list_id: collection ? snakeCase(collection) : undefined,
        item_list_name: collection,
        items: products.map((p, i) => ({
          item_id: p.id,
          item_name: p.name,
          price: p.price,
          index: i,
        })),
      }),
    );

    /**
     * Select a product from a list → GA4 `select_item`
     * @event catalog:select
     * @param {Product} product
     * @param {string} [collection]
     */
    $bus.on('catalog:select', (product, collection) =>
      useTrackEvent('select_item', {
        item_list_id: collection ? snakeCase(collection) : undefined,
        item_list_name: collection,
        items: [
          {
            item_id: product.id,
            item_name: product.name,
            price: product.price,
          },
        ],
      }),
    );

    /**
     * Quick add from a list → GA4 `add_to_cart`
     * @event catalog:add
     * @param {Product} product
     * @param {string} [collection]
     */
    $bus.on('catalog:add', (product, collection) =>
      useTrackEvent('add_to_cart', {
        currency: 'USD',
        value: product.price,
        items: [
          {
            item_id: product.id,
            item_name: product.name,
            item_list_id: collection ? snakeCase(collection) : undefined,
            item_list_name: collection,
            price: product.price,
          },
        ],
      }),
    );

    /**
     * Remove items from cart → GA4 `remove_from_cart`
     * @event cart:remove
     * @param {CartItem[]} items
     */
    $bus.on('cart:remove', items =>
      useTrackEvent('remove_from_cart', {
        currency: 'USD',
        value: items.map(i => i.total).reduce((a, b) => a + b, 0),
        items: items.map(i => ({
          item_id: i.productId,
          item_variant: i.variationId ?? undefined,
          item_name: i.name,
          price: i.price,
          quantity: i.quantity,
        })),
      }),
    );

    /**
     * Auth login → GA4 `login`
     * @event auth:login
     */
    $bus.on('auth:login', () =>
      useTrackEvent('login', {
        method: 'KYGUNCO',
      }),
    );

    /**
     * Auth registration → GA4 `sign_up`
     * @event auth:register
     */
    $bus.on('auth:register', () =>
      useTrackEvent('sign_up', {
        method: 'KYGUNCO',
      }),
    );

    /**
     * Begin checkout → GA4 `begin_checkout`
     * @event checkout:start
     * @param {CartItem[]} items
     */
    $bus.on('checkout:start', items =>
      useTrackEvent('begin_checkout', {
        currency: 'USD',
        value: items.map(i => i.total).reduce((a, b) => a + b, 0),
        items: items.map(i => ({
          item_id: i.productId,
          item_variant: i.variationId ?? undefined,
          item_name: i.name,
          price: i.price,
          quantity: i.quantity,
        })),
      }),
    );

    /**
     * Quote paid. Fetches orders and forwards them to `checkout:complete`.
     * Errors are intentionally ignored to avoid interrupting checkout flow.
     *
     * @event checkout:pay
     * @param {string} quoteId
     */
    $bus.on('checkout:pay', async quoteId => $ecommerce.fetch<OrderResponse[]>(`/quotes/${quoteId}/orders`)
      .then(orders => $bus.emit('checkout:complete', orders))
      .catch(/** fail silently **/));

    /**
     * Checkout completed → GA4 `purchase` per order
     * Emits one GA4 purchase event for each created order.
     *
     * @event checkout:complete
     * @param {OrderResponse[]} orders
     */
    $bus.on('checkout:complete', (orders) => {
      for (const order of orders) {
        useTrackEvent('purchase', {
          currency: 'USD',
          id: order.id,
          value: order.grandTotal,
          shipping: order.totals.find(t => t.description === 'Shipping and Handling')?.total,
          tax: order.totals.find(t => t.description === 'Sales Tax')?.total,
          items: order.items.map(i => ({
            item_id: i.productId,
            item_variant: i.variationId ?? undefined,
            item_name: i.name,
            price: i.price,
            quantity: i.quantity,
          })),
          email: authStore.email,
        });
      }
    });
  },
});
